import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import ProgressStepper from "../../components/ui/ProgressStepper";
import Input from "../../components/ui/Input";
import Textarea from "../../components/ui/Textarea";
import SearchableSelect from "../../components/ui/SearchableSelect";
import ChipMultiSelect from "../../components/ui/ChipMultiSelect";
import Button from "../../components/ui/Button";
import ValidatedUploadField from "../../components/ui/ValidatedUploadField";
import PdfImportDropzone from "../../components/ui/PdfImportDropzone";
import sampleOnboardingPdf from "../../assets/sample-pdf/Onboarding Sample.pdf";
import {
  getBeneficiaries,
  getBudgetRanges,
  getCountries,
  getFundingAmounts,
  getFundingTimelines,
  getFundingTypesMeta,
  getFocusAreasMeta,
  getOrgTypes,
  getYearsOperating,
} from "../../lib/api";
import { getMetaGeographic } from "../../api/profile";
import type {
  OnboardingProfileData,
  OnboardingStepId,
  SelectOption,
} from "../../features/onboarding/types";
import {
  ANNUAL_TURNOVER_OPTIONS,
  FOCUS_THEME_GROUPS,
  FUNDING_AMOUNT_OPTIONS,
  FUNDING_TIMELINE_OPTIONS,
  FUNDING_TYPE_OPTIONS,
  ONBOARDING_ACCEPTED_FILE_TYPES,
  ONBOARDING_STEPS,
  OPERATING_YEARS_OPTIONS,
  OPERATING_SCOPE_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  PRIMARY_BENEFICIARY_SUGGESTIONS,
} from "../../features/onboarding/constants";
import {
  completeOnboarding,
  resolveOnboardingRecord,
  saveOnboardingDraft,
} from "../../features/onboarding/service";
import { createEmptyOnboardingProfile } from "../../features/onboarding/mappers";
import { validateStep } from "../../features/onboarding/validation";

function FieldShell({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-2 text-sm font-semibold text-[#0F172A]">
        {label}
        {required ? " *" : ""}
      </div>
      {children}
      {error ? <div className="mt-2 text-sm text-red-600">{error}</div> : null}
    </label>
  );
}

function StepCard({
  title,
  description,
  headerAction,
  children,
}: {
  title: string;
  description: string;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[32px] border border-[#E5E7EB] bg-white p-5 shadow-sm sm:p-8">
      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-2xl font-semibold text-[#0F172A]">{title}</div>
            <p className="mt-2 text-base leading-7 text-[#475569]">{description}</p>
          </div>
          {/* To be Added later  */}
          {/* {headerAction} */}
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function getSuggestedFocusThemes(summary: string) {
  const lower = summary.toLowerCase();
  const suggestions: string[] = [];
  const rules: Array<[string[], string[]]> = [
    [["ai", "data", "machine learning"], ["AI / Data", "Technology / SaaS"]],
    [["research", "lab", "study"], ["R&D / Research", "Higher Education / Research"]],
    [["school", "student", "teacher"], ["Education", "Skills & Training"]],
    [["community", "resident", "local"], ["Community Development", "Equality & Inclusion"]],
    [["health", "clinic", "wellbeing"], ["Public Health", "Mental Health"]],
    [["climate", "sustainability", "carbon"], ["Climate & Sustainability", "Biodiversity"]],
    [["jobs", "employment", "workforce"], ["Employment Support", "Business Growth"]],
  ];

  rules.forEach(([keywords, matches]) => {
    if (keywords.some((keyword) => lower.includes(keyword))) {
      matches.forEach((match) => {
        if (!suggestions.includes(match)) suggestions.push(match);
      });
    }
  });

  return suggestions.slice(0, 5);
}

function normalizeComparable(value: string) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKC")
    .replace(/[\u2013\u2014\u2212]/g, "-")
    .replace(/[\u00A3$\u20AC,]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// A more aggressive normaliser that bridges range variations from the AI
// against the dropdown options. Handles both money ranges
// ("50k to 150k" vs "£50k-£150k", "1 million+" vs "£1M+") and time ranges
// ("Within 6-12 months" vs "6-12 months"). Used as a fallback when the
// strict comparison fails.
function normalizeMoneyRangeKey(value: string): string {
  let text = String(value || "").toLowerCase().normalize("NFKC");
  text = text.replace(/[\u2013\u2014\u2212]/g, "-");
  text = text.replace(/[\u00A3$\u20AC,]/g, "");
  // Strip common filler words at the start of a label so e.g.
  // "Within 6-12 months" becomes "6-12 months".
  text = text.replace(
    /^\s*(?:within|between|approximately|approx\.?|around|about|over|up to|up-to)\s+/g,
    ""
  );
  // Common "to" / "and" / "until" range separators -> "-"
  text = text.replace(/\s+(?:to|and|until|through|thru)\s+/g, "-");
  // Word forms of units -> short forms.
  text = text.replace(/\b(million|mn|mil)s?\b/g, "m");
  text = text.replace(/\b(thousand|grand)s?\b/g, "k");
  text = text.replace(/\b(billion|bn)s?\b/g, "b");
  // Drop any remaining whitespace so "50k to 150k" -> "50k-150k".
  text = text.replace(/\s+/g, "");
  return text.trim();
}

// Common short words / connectives we should ignore when comparing labels by
// the words they contain (e.g. "Arts, Culture & Creative Industries" vs
// "Arts & Culture").
const TOKEN_STOPWORDS = new Set([
  "and",
  "or",
  "the",
  "for",
  "with",
  "of",
  "in",
  "to",
  "on",
  "at",
  "by",
  "a",
  "an",
  "is",
  "are",
  "be",
]);

function tokenizeForMatch(value: string): string[] {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKC")
    .replace(/[^a-z0-9]+/g, " ")
    .split(/\s+/)
    .filter((token) => token.length >= 2 && !TOKEN_STOPWORDS.has(token))
    .map((token) =>
      // Lightweight singularisation so "grants" matches "grant",
      // "loans" matches "loan", etc.
      token.length > 3 && token.endsWith("s") ? token.slice(0, -1) : token
    );
}

function resolveToOptionValue(value: string, options: SelectOption[]): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const exact = options.find((option) => option.value === raw);
  if (exact) return exact.value;

  const target = normalizeComparable(raw);
  const fuzzy = options.find(
    (option) =>
      normalizeComparable(option.value) === target ||
      normalizeComparable(option.label) === target
  );
  if (fuzzy) return fuzzy.value;

  // Fallback: looser money- / time-range match (handles "50k to 150k" ->
  // "£50k-£150k", "Within 6-12 months" -> "6-12 months", etc.).
  const rangeTarget = normalizeMoneyRangeKey(raw);
  if (rangeTarget) {
    const rangeMatch = options.find(
      (option) =>
        normalizeMoneyRangeKey(option.value) === rangeTarget ||
        normalizeMoneyRangeKey(option.label) === rangeTarget
    );
    if (rangeMatch) return rangeMatch.value;
  }

  // Final fallback: token-containment match. Useful when the AI returns
  // a longer descriptive label like "Arts, Culture & Creative Industries"
  // and the dropdown option is a shorter "Arts & Culture". We match an
  // option only when EVERY significant word of that option appears in the
  // AI value, so longer AI values aren't allowed to absorb arbitrary
  // shorter options.
  const aiTokens = new Set(tokenizeForMatch(raw));
  if (aiTokens.size > 0) {
    const containmentMatch = options.find((option) => {
      const optionTokens = tokenizeForMatch(option.value);
      const labelTokens = tokenizeForMatch(option.label);
      const checkTokens = optionTokens.length ? optionTokens : labelTokens;
      if (!checkTokens.length) return false;
      return checkTokens.every((token) => aiTokens.has(token));
    });
    if (containmentMatch) return containmentMatch.value;
  }

  return "";
}

// Parses the lower-bound numeric value from a money-range label so that
// options like "Under £50k", "£50k-£150k", "£500k-£1M", and "£1M+" can be
// sorted ascending. Returns Number.MAX_SAFE_INTEGER when no number is found
// so unrecognised entries fall to the end.
function parseMoneyRangeStart(label: string): number {
  const text = String(label || "")
    .toLowerCase()
    .normalize("NFKC")
    .replace(/[\u2013\u2014\u2212]/g, "-");

  if (!text) return Number.MAX_SAFE_INTEGER;
  if (text.includes("pre-revenue") || text.includes("prerevenue")) return -1;
  if (text.startsWith("under")) return 0;

  const match = text.match(/(\d+(?:\.\d+)?)\s*(k|m|b)?/);
  if (!match) return Number.MAX_SAFE_INTEGER;

  const amount = parseFloat(match[1]);
  const unit = match[2];
  if (unit === "k") return amount * 1_000;
  if (unit === "m") return amount * 1_000_000;
  if (unit === "b") return amount * 1_000_000_000;
  return amount;
}

function sortMoneyRangeOptions(options: SelectOption[]): SelectOption[] {
  return [...options].sort(
    (a, b) => parseMoneyRangeStart(a.label) - parseMoneyRangeStart(b.label)
  );
}

export default function Onboarding() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [initializing, setInitializing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [organizationTypeOptions, setOrganizationTypeOptions] =
    useState<SelectOption[]>([]);
  const [operatingScopeOptions, setOperatingScopeOptions] = useState<SelectOption[]>([]);
  const [beneficiaryOptions, setBeneficiaryOptions] = useState<string[]>([]);
  const [annualTurnoverOptions, setAnnualTurnoverOptions] = useState<SelectOption[]>([]);
  const [operatingYearsOptions, setOperatingYearsOptions] = useState<SelectOption[]>([]);
  const [fundingTypeOptions, setFundingTypeOptions] = useState<SelectOption[]>([]);
  const [fundingAmountOptions, setFundingAmountOptions] = useState<SelectOption[]>([]);
  const [fundingTimelineOptions, setFundingTimelineOptions] = useState<SelectOption[]>([]);
  const [focusThemeGroups, setFocusThemeGroups] = useState<typeof FOCUS_THEME_GROUPS>([]);
  const [focusThemeMaxSelection, setFocusThemeMaxSelection] = useState(5);
  const [focusThemeAllowCustom, setFocusThemeAllowCustom] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState<OnboardingProfileData>(createEmptyOnboardingProfile());
  const [aiImportFile, setAiImportFile] = useState<{ name: string; size: number } | null>(
    null
  );

  const userKey = user?.uid || user?.email || "anonymous";
  const activeStep = ONBOARDING_STEPS[stepIndex];

  // Build a sanitized view of the form for validation purposes: any value
  // that is supposed to come from a dropdown but doesn't match a known
  // option is treated as empty. This prevents stale or unmatched strings
  // (e.g. left over from older drafts or AI imports) from fooling the
  // required-field check while the dropdown visually shows the placeholder.
  const validationForm = useMemo(() => {
    const isInOptions = (value: string, options: SelectOption[]) =>
      Boolean(value) && options.some((option) => option.value === value);

    const focusThemeValues = focusThemeGroups.flatMap((group) => group.options);

    return {
      ...form,
      organizationType: isInOptions(form.organizationType, organizationTypeOptions)
        ? form.organizationType
        : "",
      country: isInOptions(form.country, countryOptions) ? form.country : "",
      operatingScope: isInOptions(form.operatingScope, operatingScopeOptions)
        ? form.operatingScope
        : "",
      operatingYears: isInOptions(form.operatingYears, operatingYearsOptions)
        ? form.operatingYears
        : "",
      annualTurnoverBand: isInOptions(form.annualTurnoverBand, annualTurnoverOptions)
        ? form.annualTurnoverBand
        : "",
      fundingAmountBand: isInOptions(form.fundingAmountBand, fundingAmountOptions)
        ? form.fundingAmountBand
        : "",
      fundingTimeline: isInOptions(form.fundingTimeline, fundingTimelineOptions)
        ? form.fundingTimeline
        : "",
      fundingTypes: form.fundingTypes.filter((value) =>
        fundingTypeOptions.some((option) => option.value === value)
      ),
      focusThemes: form.focusThemes.filter((value) =>
        focusThemeValues.includes(value)
      ),
    };
  }, [
    form,
    organizationTypeOptions,
    countryOptions,
    operatingScopeOptions,
    annualTurnoverOptions,
    operatingYearsOptions,
    fundingAmountOptions,
    fundingTimelineOptions,
    fundingTypeOptions,
    focusThemeGroups,
  ]);

  const validation = useMemo(
    () => validateStep(activeStep.id, validationForm),
    [activeStep.id, validationForm]
  );
  const themeSuggestions = useMemo(() => getSuggestedFocusThemes(form.whatWeDo), [form.whatWeDo]);

  useEffect(() => {
    setErrors(validation.errors);
  }, [activeStep.id, validation.errors]);

  useEffect(() => {
    if (!user) return;
    let active = true;

    Promise.all([
      resolveOnboardingRecord(userKey),
      getCountries(true),
      getOrgTypes(true),
      getMetaGeographic(),
      getBeneficiaries(true),
      getBudgetRanges(true),
      getYearsOperating(true),
      getFundingTypesMeta(true),
      getFundingAmounts(true),
      getFundingTimelines(true),
      getFocusAreasMeta(),
    ])
      .then(
        ([
          record,
          countries,
          orgTypes,
          geographicMeta,
          beneficiaries,
          budgetRanges,
          yearsOperating,
          fundingTypes,
          fundingAmounts,
          fundingTimelines,
          focusAreasMeta,
        ]) => {
        if (!active) return;
        // TEMP: static onboarding testing mode
        // if (record.completed) {
        //   navigate("/dashboard", { replace: true });
        //   return;
        // }
        setForm(record.data);
        setCountryOptions(countries);
        setOrganizationTypeOptions(orgTypes);
        const scopeFromApi = (geographicMeta?.operating_area ?? [])
          .map((value: string) => String(value || "").trim())
          .filter(Boolean)
          .map((value: string) => ({ value, label: value }));
        setOperatingScopeOptions(scopeFromApi);
        setBeneficiaryOptions(beneficiaries.map((option) => option.label));
        setAnnualTurnoverOptions(budgetRanges);
        setOperatingYearsOptions(yearsOperating);
        setFundingTypeOptions(fundingTypes);
        setFundingAmountOptions(fundingAmounts);
        setFundingTimelineOptions(fundingTimelines);
        setFocusThemeGroups(focusAreasMeta.groups);
        setFocusThemeMaxSelection(
          typeof focusAreasMeta.maxSelection === "number" &&
            focusAreasMeta.maxSelection > 0
            ? focusAreasMeta.maxSelection
            : 5
        );
        setFocusThemeAllowCustom(
          typeof focusAreasMeta.allowCustom === "boolean"
            ? focusAreasMeta.allowCustom
            : true
        );
      })
      .catch(() => {
        if (!active) return;
        setCountryOptions([]);
        setOrganizationTypeOptions([]);
        setOperatingScopeOptions([]);
        setBeneficiaryOptions([]);
        setAnnualTurnoverOptions([]);
        setOperatingYearsOptions([]);
        setFundingTypeOptions([]);
        setFundingAmountOptions([]);
        setFundingTimelineOptions([]);
        setFocusThemeGroups([]);
        setFocusThemeMaxSelection(5);
        setFocusThemeAllowCustom(true);
      })
      .finally(() => {
        if (active) setInitializing(false);
      });

    return () => {
      active = false;
    };
  }, [navigate, user, userKey]);

  useEffect(() => {
    if (!initializing && user) {
      saveOnboardingDraft(userKey, form);
    }
  }, [form, initializing, user, userKey]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stepIndex]);

  const setField = <K extends keyof OnboardingProfileData>(
    key: K,
    value: OnboardingProfileData[K]
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleNext = async () => {
    const nextErrors = validateStep(activeStep.id, validationForm).errors;
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (stepIndex === ONBOARDING_STEPS.length - 1) {
      await handleComplete();
      return;
    }

    setStepIndex((current) => current + 1);
  };

  const handleBack = () => {
    if (stepIndex === 0) return;
    setStepIndex((current) => current - 1);
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const result = await completeOnboarding(userKey, form);
      if (!result.synced) {
        toast("Onboarding is complete. Some profile fields were saved locally and can be reviewed in Settings.");
      } else {
        toast.success("Your profile is ready.");
      }
      navigate("/dashboard/plans", { replace: true });
    } catch {
      toast.error("We couldn't finish onboarding. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSkipOptional = async () => {
    setErrors({});
    setSaving(true);
    try {
      const result = await completeOnboarding(userKey, {
        ...form,
        websiteUrl: "",
        documentUploads: [],
      });
      if (!result.synced) {
        toast("Onboarding is complete. Some profile fields were saved locally and can be reviewed in Settings.");
      } else {
        toast.success("Your profile is ready.");
      }
      navigate("/dashboard/plans", { replace: true });
    } catch {
      toast.error("We couldn't finish onboarding. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || initializing) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#F8FAFC] px-4">
        <div className="rounded-[28px] border border-[#E5E7EB] bg-white px-6 py-5 text-center shadow-sm">
          <div className="text-lg font-semibold text-[#0F172A]">Loading onboarding</div>
          <div className="mt-1 text-sm text-[#6B7280]">
            We're getting your first-login setup ready.
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#EFF8EF_0%,#F8FAFC_42%,#F8FAFC_100%)] text-[16px]">
      <div className="mx-auto max-w-3xl px-4 pb-32 pt-5 sm:px-6 sm:pt-8">
        <div className="mb-5 rounded-[32px] bg-[#0F172A] px-5 py-6 text-white shadow-lg">
          <div className="text-sm font-semibold uppercase tracking-[0.12em] text-[#D1FAE5]">
            First Login Setup
          </div>
          <h1 className="mt-2 text-3xl font-semibold leading-tight">
            Build your funding profile once, then edit it anytime in Settings.
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-[#CBD5E1]">
            This short wizard helps ROOTT tailor your funding matches, autofill profile data,
            and prepare your workspace for grant writing.
          </p>
        </div>

        <div className="sticky top-4 z-20 mb-5">
          <ProgressStepper
            steps={ONBOARDING_STEPS.map((step) => ({ id: step.id, title: step.title }))}
            activeStep={activeStep.id}
          />
        </div>

        {activeStep.id === "basics" && (
          <StepCard
            title="Basics"
            description="Start with the essentials we need to match you accurately."
            headerAction={
              <div className="flex items-center gap-2 rounded-xl border border-[#D6DADF] bg-[#F8FAFC] px-3 py-2">
                <span className="material-icons text-[18px] text-[#166534]">description</span>
                <span className="max-w-[180px] truncate text-sm font-medium text-[#0F172A]">
                  Sample PDF
                </span>
                <a
                  href={sampleOnboardingPdf}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Preview sample PDF"
                  title="Preview sample PDF"
                  className="flex rounded-md p-1 text-[#475569] transition hover:bg-white hover:text-[#0F172A]"
                >
                  <span className="material-icons text-[18px]">visibility</span>
                </a>
                <a
                  href={sampleOnboardingPdf}
                  download="Onboarding Sample.pdf"
                  aria-label="Download sample PDF"
                  title="Download sample PDF"
                  className="flex rounded-md p-1 text-[#475569] transition hover:bg-white hover:text-[#0F172A]"
                >
                  <span className="material-icons text-[18px]">download</span>
                </a>
              </div>
            }
          >
            <PdfImportDropzone
              uploadedFile={aiImportFile}
              onUploadedFileChange={setAiImportFile}
              extraDetails={{
                mission: form.whatWeDo,
                focus: form.focusThemes.join(", "),
              }}
              onDataExtracted={(data) => {
                setForm((current) => {
                  // Normalizes a single-select value against the dropdown's
                  // option list. If the AI returned a value that does not
                  // match any option, return undefined so we keep `current`
                  // instead of writing an unmatched string that would fool
                  // validation while showing the placeholder visually.
                  const matchSingle = (
                    value: string | undefined,
                    options: SelectOption[]
                  ): string | undefined => {
                    if (!value) return undefined;
                    const matched = resolveToOptionValue(value, options);
                    return matched || undefined;
                  };

                  const matchMulti = (
                    values: string[] | undefined,
                    options: SelectOption[]
                  ): string[] | undefined => {
                    if (!values?.length) return undefined;
                    const matched = new Set<string>();

                    const tryMatch = (candidate: string) => {
                      const result = resolveToOptionValue(candidate, options);
                      if (result) matched.add(result);
                    };

                    for (const value of values) {
                      // Try the whole string first - handles cases where a
                      // single label contains internal commas/ampersands
                      // (e.g. "Arts & Culture").
                      tryMatch(value);
                      // Then try comma- / semicolon-separated fragments so a
                      // single AI string like "Arts, Culture & Creative
                      // Industries, bio diversity" can also surface
                      // additional themes (e.g. "Biodiversity").
                      const fragments = value
                        .split(/[,;]+/)
                        .map((piece) => piece.trim())
                        .filter(Boolean);
                      if (fragments.length > 1) {
                        for (const fragment of fragments) {
                          tryMatch(fragment);
                        }
                      }
                    }

                    return matched.size ? Array.from(matched) : undefined;
                  };

                  const focusThemeOptions: SelectOption[] = focusThemeGroups.flatMap(
                    (group) =>
                      group.options.map((option) => ({ value: option, label: option }))
                  );

                  const next: OnboardingProfileData = { ...current };

                  // Free-text fields (no dropdown) - apply directly when filled.
                  if (data.organizationName?.trim()) {
                    next.organizationName = data.organizationName.trim();
                  }
                  if (data.postcode?.trim()) {
                    next.postcode = data.postcode.trim();
                  }
                  if (data.websiteUrl?.trim()) {
                    next.websiteUrl = data.websiteUrl.trim();
                  }
                  if (data.whatWeDo?.trim()) {
                    next.whatWeDo = data.whatWeDo.trim();
                  }

                  // Single-select dropdowns - only apply when the AI value
                  // matches a known option.
                  const orgType = matchSingle(data.organizationType, organizationTypeOptions);
                  if (orgType) next.organizationType = orgType;

                  const country = matchSingle(data.country, countryOptions);
                  if (country) next.country = country;

                  const opScope = matchSingle(data.operatingScope, operatingScopeOptions);
                  if (opScope) next.operatingScope = opScope;

                  const turnover = matchSingle(data.annualTurnoverBand, annualTurnoverOptions);
                  if (turnover) next.annualTurnoverBand = turnover;

                  const amountBand = matchSingle(
                    data.fundingAmountBand,
                    fundingAmountOptions
                  );
                  if (amountBand) next.fundingAmountBand = amountBand;

                  const timeline = matchSingle(
                    data.fundingTimeline,
                    fundingTimelineOptions
                  );
                  if (timeline) next.fundingTimeline = timeline;

                  const operatingYears = matchSingle(
                    data.operatingYears,
                    operatingYearsOptions
                  );
                  if (operatingYears) next.operatingYears = operatingYears;

                  // Multi-select fields - keep only values matching a known option.
                  const fundingTypes = matchMulti(data.fundingTypes, fundingTypeOptions);
                  if (fundingTypes) next.fundingTypes = fundingTypes;

                  const focusThemes = matchMulti(data.focusThemes, focusThemeOptions);
                  if (focusThemes) next.focusThemes = focusThemes;

                  // Primary beneficiaries can be free-form chips, so accept as-is.
                  if (data.primaryBeneficiaries?.length) {
                    next.primaryBeneficiaries = data.primaryBeneficiaries;
                  }

                  return next;
                });
              }}
            />

            <FieldShell label="Organization Type" required error={errors.organizationType}>
              <SearchableSelect
                options={organizationTypeOptions}
                value={form.organizationType}
                onChange={(value) => setField("organizationType", value)}
                placeholder="Choose organization type"
              />
            </FieldShell>

            <FieldShell label="Organization Name" required error={errors.organizationName}>
              <Input
                value={form.organizationName}
                onChange={(event) => setField("organizationName", event.target.value)}
                placeholder="Enter your organization name"
                className="min-h-[44px] text-base"
              />
            </FieldShell>

            <div className="grid gap-5 sm:grid-cols-2">
              <FieldShell label="Country" required error={errors.country}>
                <SearchableSelect
                  options={countryOptions}
                  value={form.country}
                  onChange={(value) => setField("country", value)}
                  placeholder="Search country"
                />
              </FieldShell>

              <FieldShell label="Postcode" required error={errors.postcode}>
                <Input
                  value={form.postcode}
                  onChange={(event) => setField("postcode", event.target.value)}
                  placeholder="Enter postcode"
                  className="min-h-[44px] text-base"
                />
              </FieldShell>
            </div>

            <FieldShell label="Operating Scope" required error={errors.operatingScope}>
              <SearchableSelect
                options={operatingScopeOptions}
                value={form.operatingScope}
                onChange={(value) => setField("operatingScope", value)}
                placeholder="Select scope"
              />
            </FieldShell>
          </StepCard>
        )}

        {activeStep.id === "coreProfile" && (
          <StepCard
            title="Core Profile"
            description="Tell us what you do, how established you are, and who benefits."
          >
            <FieldShell label="What We Do" required error={errors.whatWeDo}>
              <Textarea
                value={form.whatWeDo}
                onChange={(event) => setField("whatWeDo", event.target.value)}
                placeholder="We provide AI tools to help small businesses access funding."
                className="min-h-[130px] text-base leading-7"
              />
            </FieldShell>

            <div className="grid gap-5 sm:grid-cols-2">
              <FieldShell
                label="How long have you been operating?"
                required
                error={errors.operatingYears}
              >
                <SearchableSelect
                  options={operatingYearsOptions}
                  value={form.operatingYears}
                  onChange={(value) => setField("operatingYears", value)}
                  placeholder="Choose operating duration"
                />
              </FieldShell>

              <FieldShell
                label="Annual Turnover Band"
                required
                error={errors.annualTurnoverBand}
              >
                <SearchableSelect
                  options={annualTurnoverOptions}
                  value={form.annualTurnoverBand}
                  onChange={(value) => setField("annualTurnoverBand", value)}
                  placeholder="Choose turnover band"
                />
              </FieldShell>
            </div>

            <FieldShell
              label="Primary Beneficiaries"
              required
              error={errors.primaryBeneficiaries}
            >
              <ChipMultiSelect
                options={beneficiaryOptions}
                value={form.primaryBeneficiaries}
                onChange={(value) => setField("primaryBeneficiaries", value)}
                allowCustom
                customLabel="Add custom beneficiary"
                placeholder="Select or add beneficiaries"
              />
            </FieldShell>
          </StepCard>
        )}

        {activeStep.id === "strategicFocus" && (
          <StepCard
            title="Strategic Focus"
            description="Pick up to five areas of focus so ROOTT can tune your matches without getting noisy."
          >
            {themeSuggestions.length > 0 && (
              <div className="rounded-3xl border border-[#D6F5DD] bg-[#F3FBF4] p-4">
                <div className="text-sm font-semibold text-[#166534]">
                  Suggested from your summary
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {themeSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => {
                        const merged = Array.from(new Set([...form.focusThemes, suggestion])).slice(0, 5);
                        setField("focusThemes", merged);
                      }}
                      className="min-h-[40px] rounded-full border border-[#BFE4C5] bg-white px-3 py-2 text-sm text-[#166534]"
                    >
                      {suggestion}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const merged = Array.from(new Set([...form.focusThemes, ...themeSuggestions])).slice(0, 5);
                      setField("focusThemes", merged);
                    }}
                    className="min-h-[40px] rounded-full bg-[#166534] px-4 py-2 text-sm font-medium text-white"
                  >
                    Accept all
                  </button>
                </div>
              </div>
            )}

            <FieldShell
              label="What areas do you operate in or seek funding for?"
              required
              error={errors.focusThemes}
            >
              <ChipMultiSelect
                groups={focusThemeGroups}
                value={form.focusThemes}
                onChange={(value) => setField("focusThemes", value)}
                maxSelected={focusThemeMaxSelection}
                allowCustom={focusThemeAllowCustom}
                customLabel="Add custom area"
                placeholder={`Choose up to ${focusThemeMaxSelection} areas of focus`}
                onLimitHit={(message) => toast.error(message)}
              />
            </FieldShell>
          </StepCard>
        )}

        {activeStep.id === "fundingGoals" && (
          <StepCard
            title="Funding Goals"
            description="Set the kinds of funding you want and the urgency behind your search."
          >
            <FieldShell label="Funding Types" required error={errors.fundingTypes}>
              <ChipMultiSelect
                options={fundingTypeOptions.map((option) => option.value)}
                value={form.fundingTypes}
                onChange={(value) => setField("fundingTypes", value)}
                placeholder="Choose funding types"
              />
            </FieldShell>

            <div className="grid gap-5 sm:grid-cols-2">
              <FieldShell
                label="Funding Amount Band"
                required
                error={errors.fundingAmountBand}
              >
                <SearchableSelect
                  options={fundingAmountOptions}
                  value={form.fundingAmountBand}
                  onChange={(value) => setField("fundingAmountBand", value)}
                  placeholder="Choose amount band"
                />
              </FieldShell>

              <FieldShell label="Funding Timeline" required error={errors.fundingTimeline}>
                <SearchableSelect
                  options={fundingTimelineOptions}
                  value={form.fundingTimeline}
                  onChange={(value) => setField("fundingTimeline", value)}
                  placeholder="Choose timeline"
                />
              </FieldShell>
            </div>
          </StepCard>
        )}

        {activeStep.id === "boostMatches" && (
          <StepCard
            title="Boost Your Matches"
            description="This last step is optional. Add a website or upload supporting files to give ROOTT extra context."
          >
            <FieldShell label="Website URL" error={errors.websiteUrl}>
              <Input
                value={form.websiteUrl}
                onChange={(event) => setField("websiteUrl", event.target.value)}
                placeholder="https://example.org"
                className="min-h-[44px] text-base"
              />
            </FieldShell>

            <FieldShell label="Supporting Documents">
              <ValidatedUploadField
                value={form.documentUploads}
                onChange={(value) => setField("documentUploads", value)}
                accept={ONBOARDING_ACCEPTED_FILE_TYPES}
                helperText="PDF, DOCX, and PPTX files are supported."
              />
            </FieldShell>
          </StepCard>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E5E7EB] bg-white/95 px-4 py-3 shadow-[0_-12px_32px_rgba(15,23,42,0.08)] backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <Button
            variant="outline"
            className="min-h-[48px] min-w-[96px] rounded-2xl"
            onClick={handleBack}
            disabled={stepIndex === 0 || saving}
          >
            Back
          </Button>

          {activeStep.id === "boostMatches" && (
            <Button
              variant="ghost"
              className="min-h-[48px] rounded-2xl text-[#475569]"
              onClick={handleSkipOptional}
              disabled={saving}
            >
              Skip for now
            </Button>
          )}

          <Button
            className="min-h-[52px] flex-1 rounded-2xl text-base font-semibold"
            onClick={handleNext}
            disabled={!validation.isValid || saving}
          >
            {saving
              ? "Saving..."
              : activeStep.id === "boostMatches"
                ? "Show my funding matches"
                : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
